import { FC, ReactNode, useMemo } from 'react';
import { Alert, Box } from '@mui/material';

import { executeSqlQuery } from '@api/services/ingest';
import { QueryTemplate } from '@components/base';
import useLoad from '@hooks/useLoad';

import TitleDataTree from './TitleDataTree';

type SqlDataTreeProps = {
  enabled: boolean;
  header?: ReactNode;
  masterId?: string;
  playbackOptionId?: string;
};

const escapeSqlLiteral = (value: string) => value.replaceAll("'", "''");

const parseJsonStrings = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(parseJsonStrings);

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, parseJsonStrings(nestedValue)])
    );
  }

  if (typeof value === 'string' && ['{', '['].includes(value.trimStart()[0])) {
    try {
      return parseJsonStrings(JSON.parse(value));
    } catch {
      // The value only resembles JSON; display the original string.
    }
  }

  return value;
};

const SqlDataTree: FC<SqlDataTreeProps> = ({ enabled, header, masterId, playbackOptionId }) => {
  const isPlaybackOptionView = Boolean(playbackOptionId);
  const id = isPlaybackOptionView ? playbackOptionId : masterId;
  const table = isPlaybackOptionView ? 'PlaybackOption' : 'Title';
  const sql = id ? `SELECT * FROM "${table}" WHERE "Id" = '${escapeSqlLiteral(id)}'` : '';
  const queryResult = useLoad(() => executeSqlQuery(sql), ['detail-panel-sql', table, id], {
    enabled: enabled && Boolean(id),
    refetchOnSearchLanguageChange: false,
    sanitizeResponse: false,
  });
  const sqlData = useMemo(
    () => (queryResult.data?.[0] ? parseJsonStrings(queryResult.data[0]) : undefined),
    [queryResult.data]
  );

  return (
    <QueryTemplate queryResult={{ ...queryResult, context: 'SQL data' }}>
      {sqlData ? (
        <TitleDataTree data={sqlData as object} header={header} />
      ) : (
        <Box p={2}>
          <Alert severity='info'>No SQL data found.</Alert>
        </Box>
      )}
    </QueryTemplate>
  );
};

export default SqlDataTree;
